<template>
  <Navbar title="Registrar pago" :class="{'show' : showNavbar}">
    <template v-slot:close>
      <div @click="$emit('close')" class="close">
        Cerrar
      </div>
    </template>
    <template v-slot:content>
      <template v-if="!data.paid">
        <!--Highlight-->
        <div class="bills__high bills__high--green">
          <div class="item">
            <span class="item__label">Monto a pagar</span>
            <span class="item__rent">{{
                Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: data.currency ? data.currency.code : 'PEN'
                }).format(data.total)
              }}</span>
          </div>
        </div>
        <!--End-->
        <!--Payment date-->
        <div class="form-group">
          <label for="paymentDate">Fecha de pago</label>
          <v-date-picker
            id="start"
            name="start"
            v-model="document.paymentDate"
            color="red"
            is-expanded
          />
          <span v-if="this.validationErrors.paymentDate" class="error">{{ this.validationErrors.paymentDate }}</span>
        </div>
        <!--Voucher-->
        <div class="form-group">
          <input @change="updateImage" class="file" ref="uploadVoucher" type="file" accept="image/*" multiple>
          <button class="btn btn--outline btn--w100" @click="$refs.uploadVoucher.click()">Adjuntar comprobantes</button>
          <div v-if="previews.length" class="previews">
            <div v-for="(img, index) in previews" :key="index" class="previews__item">
              <img :src="img" alt="Voucher">
              <span class="previews__remove" @click="removeImage(index)">&times;</span>
            </div>
          </div>
        </div>
      </template>
      <template v-else>
        <!--Highlight-->
        <div class="bills__high bills__high--green">
          <div class="item">
            <span class="item__label">Total pagado</span>
            <span class="item__rent">{{
                Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: document.currency ? document.currency.code : 'PEN'
                }).format(document.total)
              }}</span>
          </div>
          <div class="item">
            <span class="item__label">Fecha de pago</span>
            <span class="item__rent">{{ moment(document.paymentDate).lang('es').format('DD[/]MMMM[/]YYYY') }}</span>
          </div>
        </div>
        <!--End-->
        <!--Vouchers-->
        <div v-if="voucherList.length" class="media-grid">
          <div v-for="(img, index) in voucherList" :key="index" class="media">
            <img :src="img" alt="Voucher de pago">
            <div @click="openImg(img)" class="media__open">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                   class="feather feather-external-link">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                <polyline points="15 3 21 3 21 9"></polyline>
                <line x1="10" y1="14" x2="21" y2="3"></line>
              </svg>
            </div>
          </div>
        </div>
      </template>
    </template>
    <template v-slot:footer>
      <button :disabled="progress || data.paid" :class="{'is-loading': progress}" @click="save" class="btn">
        <span v-if="!progress">Registrar pago</span>
      </button>
    </template>
  </Navbar>
  <Shadow :class="{'show' : showNavbar}"/>
</template>

<script>
import Navbar from '@/components/Navbar'
import Shadow from '@/components/Shadow'
import moment from 'moment'

import * as yup from 'yup'

import { FieldValue, db, storage } from '@/firebase/firebaseConfig'

yup.setLocale({
  mixed: {
    required: 'Este campo es obligatorio'
  },
  number: {
    positive: 'Debe ser positivo',
    integer: 'Debe ser entero',
    truncate: 'Formato incorrecto'
  }
})

export default {
  name: 'PayTax',
  components: {
    Navbar,
    Shadow
  },
  props: {
    showNavbar: {
      type: Boolean,
      default: () => {
        return false
      }
    },
    data: {
      type: Object,
      default: () => {
        return { currency: { code: '' } }
      }
    }
  },
  watch: {
    showNavbar (value) {
      if (value) {
        this.document = {}
        this.validationErrors = {}
        this.previews = []
        this.attachments = []
        if (this.data.paid) {
          this.document = {
            ...this.data
          }
        }
      }
    }
  },
  data () {
    return {
      document: {},
      progress: false,
      schema: yup.object().shape({
        paymentDate: yup.date().required().label('Fecha de pago')
      }),
      include: true,
      validationErrors: {},
      attachments: [],
      previews: [],
      moment: moment
    }
  },
  computed: {
    voucherList () {
      if (this.document.vouchers && this.document.vouchers.length) return this.document.vouchers
      if (this.document.voucher) return [this.document.voucher]
      return []
    }
  },
  methods: {
    /**
     * Save data in firestore
     * */
    async save () {
      try {
        this.progress = true
        await this.schema.validate({ ...this.document }, { abortEarly: false })
        const obj = {
          paymentDate: this.document.paymentDate,
          paid: true,
          updatedAt: FieldValue.serverTimestamp(),
          vouchers: []
        }
        if (this.attachments.length) {
          obj.vouchers = await Promise.all(this.attachments.map(f => this.submitFile(f)))
        }
        await db.collection('estates').doc(this.data.estate.id).collection('propertyTaxes').doc(this.data.id).update({
          ...obj
        })
        obj.id = this.data.id
        this.$emit('update', obj)
        this.$emit('close')
      } catch (err) {
        console.log(err)
        if (err.inner) {
          err.inner.forEach(error => {
            if (error.path) {
              this.validationErrors[error.path] = error.message
            }
          })
        } else {
          this.$toast.error('Error al registrar pago')
        }
      } finally {
        this.progress = false
      }
    },
    updateImage (input) {
      if (input.target.files) {
        Array.from(input.target.files).forEach(file => {
          this.attachments.push(file)
          const reader = new FileReader()
          reader.onload = e => {
            this.previews.push(e.target.result)
          }
          reader.readAsDataURL(file)
        })
        this.$refs.uploadVoucher.value = ''
      }
    },
    removeImage (index) {
      this.attachments.splice(index, 1)
      this.previews.splice(index, 1)
    },
    /**
     * Save doc in firebase storage
     * @param doc
     * @returns {Promise<unknown>}
     */
    submitFile (doc) {
      return new Promise((resolve, reject) => {
        const docName = Math.random().toString(36).substr(1, 20)
        const fileExtension = '.' + doc.name.split('.').pop()
        const docNameFinal = docName + fileExtension

        const storageRef = storage.ref(`vouchers/estates/${docNameFinal}`)
        storageRef.put(doc)
          .then((snapshot) => {
            snapshot.ref.getDownloadURL().then((url) => {
              resolve(url)
            })
          })
          .catch((error) => {
            reject(error)
          })
      })
    },
    openImg (img) {
      window.open(img, '_blank')
    }
  }
}
</script>

<style scoped>

</style>
